import { BrowserRouter, Route, Link } from "react-router-dom";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { Blog } from "./Pages/Blog";
import { SingleBlog } from "./Pages/BlogDetails";
import TalentDetails from "./Pages/CBT/TalentDetails";
import HomeAdminDashboard from "./Pages/Dashboard";
import EditBlog from "./Pages/EditPost";
import EnterResult from "./Pages/HREnterResult/EnterResult";
import SearchTalent from "./Pages/HREnterResult/SearchTalent";
import MyPost from "./Pages/MyPost";
import CompanyDetails from "./Pages/Project/CompanyDetails";
import SelectCatAut from "./Pages/SelectCatAut";
import ClientRequest from "./Pages/Talent/ClientRequest";

import Talents from "./Pages/Talent/Talents";
import MatchedTalents from "./Pages/Talent/MatchedTalents";
import TalentPayment from "./Pages/Talent/TalentPayment";
import { Header } from "./components/Layouts/Header";
import { SideBar } from "./components/Layouts/Sidebar";
import useHandleLogout from "./reactWrapper/hooks/useHandleLogout";
import { useUserProfileMutation } from "./reactWrapper/endpoints/auth/auth.api";
import {
  addUserProfile,
  selectUserState,
} from "./reactWrapper/Redux/auth/authSlice";
import { TOKEN } from "./helper/constant";
import { Fragment, useEffect, useRef, useState } from "react";
import PageLoader from "./components/Loader/PageLoader";
import SessionExpiredModal from "./components/SessionExpiredModal";
import {
  endSession,
  hydrateFromStorage,
  persistTokenPair,
} from "./reactWrapper/auth/refreshManager";
import { exchangeCode, fetchMe } from "./reactWrapper/auth/session.api";
import { store } from "./reactWrapper/Redux/store";
import {
  useAppDispatch,
  useTypedSelector,
} from "./reactWrapper/Redux/utilities";
import IncompleteProfile from "./Pages/Talent/IncompleteProfile";
import IncompleteProfileDetails from "./Pages/Talent/IncompleteProfileDetails";
import Settings from "./Pages/Settings/Settings";
import { LinkProvider } from "./components/ui-x";
import type { UIXLinkProps } from "./components/ui-x";

// eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJ1aWQiOjUsImlhdCI6MTcxOTE5Nzk1N30.Q3109j7z-2kpUXLEutZ_-IJjf2rLV7hVFBDxJy0m-CA
import RoleLibrary from "./Pages/Settings/RoleLibrary";
import CbtQuestionGenerator from "./Pages/CBT/CbtQuestionGenerator";
import CbtQuestionRun from "./Pages/CBT/CbtQuestionRun";
import AllKyb from "./Pages/Client/AllKyb";
import KybDetails from "./Pages/Client/KybDetails";
import Partners from "./Pages/Partner/Partners";
import PartnerDetails from "./Pages/Partner/PartnerDetails";
import PendingJobs from "./Pages/Client/PendingJobs";
import ContentCreatorPage from "./Pages/Talent/ContentCreators";
import ContentCreatorDetails from "./Pages/Talent/ContentCreatorDetails";

/* ui-x renders <UIXLink>; this teaches it what a link is in this app.
   react-router-dom is v5 here, but v5 and v6 share the <Link to> API. */
const RouterLink = ({ to, children, ...rest }: UIXLinkProps) => {
  const external =
    to.startsWith("http") || to.startsWith("//") || to.startsWith("mailto:");

  return external ? (
    <a href={to} {...rest}>
      {children}
    </a>
  ) : (
    <Link to={to} {...rest}>
      {children}
    </Link>
  );
};

function App() {
  const userState = useTypedSelector(selectUserState);
  const dispatch = useAppDispatch();
  const { getProfile } = useUserProfileMutation();
  const { handleLogout } = useHandleLogout();
  const [collapsed, setCollapsed] = useState(false);

  /* Session bootstrap. Four ways in, checked in this order:
       1. ?code=   one-time handoff from welcome -> /auth/exchange (new)
       2. stored refresh token                   -> /auth/me       (new)
       3. ?enter= or stored legacy JWT           -> getUserProfile (legacy)
       4. nothing                                -> off to welcome
     The admin app's legacy param is ?enter= (not ?token=); both are read and
     stripped from the URL before any branch runs, whichever path ends up
     used: a live credential in the address bar ends up in browser history
     and in anything that records page URLs. replaceState rewrites the
     current history entry, so there is no back-button copy either. Other
     params are preserved. */
  /* React 18 StrictMode runs this effect twice in development (mount,
     simulated unmount, mount again). The first run strips ?code= from the
     URL and starts the exchange; a second run would then find no code and
     no stored session yet, and fall through to logout, bouncing a freshly
     logged-in user back to welcome. Production builds run it once. */
  const bootstrapped = useRef(false);
  useEffect(() => {
    if (bootstrapped.current) return;
    bootstrapped.current = true;

    const params = new URLSearchParams(window.location.search);
    const code = params.get("code")?.trim() ?? "";
    const legacyToken = params.get("enter")?.trim() ?? "";

    if (params.has("code") || params.has("enter")) {
      params.delete("code");
      params.delete("enter");
      const query = params.toString();
      window.history.replaceState(
        {},
        document.title,
        window.location.pathname +
          (query ? `?${query}` : "") +
          window.location.hash,
      );
    }

    /* The old easyHttp helper cached the JWT under its own key; nothing
       reads it any more, so drop it rather than leave a stray credential. */
    localStorage.removeItem("authless");

    if (code) {
      exchangeCode(code)
        .then(({ accessToken, refreshToken, expiresIn, user }) => {
          persistTokenPair({ accessToken, refreshToken, expiresIn });
          dispatch(addUserProfile({ accessToken, refreshToken, user }));
        })
        .catch((error: any) => {
          /* CODE_USED / CODE_EXPIRED / CODE_INVALID are all terminal: the
             code is single-use and lives 60s, so there is nothing to retry.
             A network failure leaves the loader; the user can reload. */
          if (error?.response?.status === 401) endSession("unknown");
        });
      return;
    }

    if (hydrateFromStorage()) {
      if (userState.accessToken && userState.user) return;

      fetchMe()
        .then((user) => {
          const { accessToken, refreshToken } = (store.getState() as any).auth;
          dispatch(addUserProfile({ accessToken, refreshToken, user }));
        })
        .catch(() => {
          /* A 401 here means the interceptor already tried to refresh and
             failed, and endSession has raised the modal. Anything else is
             a network or server fault, and the session is still valid. */
        });
      return;
    }

    if (legacyToken) {
      const formData = new FormData();
      const token = legacyToken;
      formData.append("userToken", token);
      /* Written before the call so the request interceptor (which falls
         back to storage) can attach it. */
      localStorage.setItem(TOKEN, token ?? "");
      getProfile(formData, {
        onSuccess: (res) => {
          const userInfo = {
            accessToken: res.accessToken,
            user: {
              ...res.user.data[0],
              extraDetail: res.user.data[1],
            },
          };
          dispatch(addUserProfile(userInfo));
        },
        /* A handoff token the API rejects is dead on arrival -- there is no
           refresh token at this point, so the only way forward is a fresh
           login. Swallowing this used to strand the user on the loader
           below, forever. */
        onError: (error: any) => {
          if (error?.response?.status === 401) endSession("unknown");
        },
      });
    } else if (localStorage.getItem(TOKEN)) {
      if (userState.accessToken) return;

      const formData = new FormData();
      formData.append("userToken", localStorage.getItem(TOKEN) ?? "");
      getProfile(formData, {
        onSuccess: (res) => {
          const userInfo = {
            accessToken: res.accessToken,
            user: {
              ...res.user.data[0],
              extraDetail: res.user.data[1],
            },
          };
          dispatch(addUserProfile(userInfo));
        },
        /* A stored legacy JWT (no refresh token alongside it) that the API
           now rejects. Nothing to refresh with; raise the modal instead of
           the endless loader. Sessions started via ?code= never come
           through here -- they hydrate from the stored pair above. */
        onError: (error: any) => {
          if (error?.response?.status === 401) endSession("unknown");
        },
      });
    } else {
      handleLogout();
    }
  }, []);

  /* The modal has to render alongside the loader, not inside the tree below
     it: an expired session never satisfies this guard, so anything rendered
     only past it could never be seen. */
  if (userState.accessToken === null || userState?.user?.role !== "admin")
    return (
      <Fragment>
        <SessionExpiredModal />
        <PageLoader />
      </Fragment>
    );

  return (
    <>
      <div className="flex min-h-screen bg-surface">
        <BrowserRouter>
          <ToastContainer />
          <SessionExpiredModal />
          <SideBar collapsed={collapsed} setCollapsed={setCollapsed} />
          <LinkProvider component={RouterLink}>
            <div className="flex min-w-0 flex-1 flex-col">
              <Header />
              <main className="min-w-0 flex-1 p-4 sm:p-6 lg:p-8">
                <Route path="/postblog" exact component={Blog} />
                <Route path="/mypost" exact component={MyPost} />
                <Route
                  path="/createauthorcategory"
                  exact
                  component={SelectCatAut}
                />

                <Route path="/mypost/:postId" exact component={SingleBlog} />
                <Route path="/editpost/:postId" exact component={EditBlog} />
                <Route path="/search-talent" exact component={SearchTalent} />
                <Route
                  path="/search-talent/result"
                  exact
                  component={EnterResult}
                />
                <Route path="/talents" exact component={Talents} />
                <Route path="/client-request" exact component={ClientRequest} />
                <Route
                  path="/matched-talents/:jobId"
                  exact
                  component={MatchedTalents}
                />
                <Route path="/payments" exact component={TalentPayment} />
                <Route
                  path="/client-details/:id"
                  exact
                  component={CompanyDetails}
                />
                <Route path="/client/kyb" exact component={AllKyb} />
                <Route path="/client/kyb/:id" exact component={KybDetails} />
                <Route path="/partners" exact component={Partners} />
                <Route path="/partners/:id" exact component={PartnerDetails} />
                <Route
                  path="/client/jobs/pending"
                  exact
                  component={PendingJobs}
                />
                <Route
                  path="/matched-talents/cbt-profile/:id"
                  exact
                  component={TalentDetails}
                />
                <Route
                  path="/talent-incomplete-profile"
                  exact
                  component={IncompleteProfile}
                />

                <Route
                  path="/micro-content/creator"
                  exact
                  component={ContentCreatorPage}
                />

                <Route
                  path="/content-creator/:id"
                  exact
                  component={ContentCreatorDetails}
                />

                <Route
                  path="/incomplete-profile/:id"
                  exact
                  component={IncompleteProfileDetails}
                />

                <Route path="/settings" exact component={Settings} />

                <Route path="/cbt/roles" exact component={RoleLibrary} />
                <Route
                  path="/cbt/questions"
                  exact
                  component={CbtQuestionGenerator}
                />
                <Route
                  path="/cbt/questions/:id"
                  exact
                  component={CbtQuestionRun}
                />
                <Route path="/" exact component={HomeAdminDashboard} />
              </main>
            </div>
          </LinkProvider>
        </BrowserRouter>
      </div>
    </>
  );
}

export default App;
